import { useQueries } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import type { Issue } from '../../api/schema'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { DocumentPreviewCard } from '../documents/DocumentPreviewCard'
import { issueSourceDocumentUids } from './issueSources'

interface IssueSourceCardProps {
  orgCode: string
  issue: Issue
}

/**
 * The documents a ticket stems from, at the top of the ticket (spec
 * 2026-10-11-26): what the AI read to raise it, one click away. Only the rows
 * that loaded are shown: a document out of the reader's scope or deleted
 * answers 404 and hides its row, and no loaded row means no card.
 */
export function IssueSourceCard({ orgCode, issue }: IssueSourceCardProps) {
  const { t } = useTranslation('issues')
  const contractUid = issue.target_entity_type === 'contract' ? issue.target_entity_uid ?? '' : ''
  // Same query as IssueTargetLink's, so one request serves both.
  const contract = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/contracts/{contractUid}',
    { params: { path: { orgCode, contractUid } } },
    { enabled: Boolean(contractUid) },
  )
  const uids = issueSourceDocumentUids(issue, contract.data)
  const docs = useQueries({
    queries: uids.map((documentUid) => ({
      // Same key as useDocument.
      ...$api.queryOptions('get', '/api/v1/organizations/{orgCode}/documents/{documentUid}', {
        params: { path: { orgCode, documentUid } },
      }),
      retry: false,
    })),
  })
  const loaded = docs.flatMap((q, i) => (q.data ? [{ uid: uids[i], doc: q.data }] : []))
  if (loaded.length === 0) return null

  return (
    <Card data-testid="issue-source-card">
      <CardHeader>
        <CardTitle className="text-base">
          {t('detail.source_documents_title', { count: loaded.length })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loaded.map(({ uid, doc }) => (
          <DocumentPreviewCard
            key={uid}
            org={orgCode}
            documentUid={uid}
            document={doc}
            defaultOpen={false}
            testidPrefix="issue-source"
          />
        ))}
      </CardContent>
    </Card>
  )
}
